import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowUpRight, Check, Loader2, Crown } from "lucide-react";
import { toast } from "sonner";

const PREMIUM_FEATURES = [
  "Unlimited AI selfie analyses",
  "Photoreal virtual try-on (Nano Banana)",
  "Stylist & barber installation guides",
  "Grooming protocols by hairstyle",
  "Save unlimited favorites",
  "Trending styles by country",
  "Priority new-style drops",
];
const FREE_FEATURES = [
  "3 AI analyses per month",
  "Browse full style gallery",
  "View stylist & maintenance guides",
  "Save up to 10 favorites",
];

export default function Pricing() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const subscribe = async () => {
    if (!user) {
      const redirectUrl = window.location.origin + "/dashboard";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    if (user.is_premium) {
      toast.success("You're already a Premium member.");
      navigate("/dashboard");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/payments/checkout", { origin_url: window.location.origin });
      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="crown-container py-12 md:py-20" data-testid="pricing-page">
      <div className="text-center max-w-2xl mx-auto mb-16">
        <p className="font-overline mb-4">The membership</p>
        <h1 className="font-serif text-5xl sm:text-6xl leading-tight mb-6">
          Choose your <span className="italic text-gold">crown</span>.
        </h1>
        <p className="text-ash">Two tiers. No commitment. Cancel anytime.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {/* FREE */}
        <div className="border border-bone/15 p-10" data-testid="plan-free">
          <p className="font-overline text-ash mb-6">Free</p>
          <p className="font-serif text-5xl mb-2">$0</p>
          <p className="text-ash mb-10">Forever · taste the atelier</p>
          <ul className="space-y-3 mb-10">
            {FREE_FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-3 text-bone text-sm">
                <Check size={14} className="text-gold mt-1" strokeWidth={1.5} /> {f}
              </li>
            ))}
          </ul>
          <button className="crown-btn-ghost w-full justify-center" disabled>
            {!user ? "Get started free" : "Current plan"}
          </button>
        </div>

        {/* PREMIUM */}
        <div className="bg-obsidian border border-gold/40 p-10 relative" data-testid="plan-premium">
          <div className="absolute -top-3 left-10 bg-gold text-espresso font-overline text-[0.65rem] px-3 py-1">Most chosen</div>
          <p className="font-overline text-gold mb-6 flex items-center gap-2">
            <Crown size={14} strokeWidth={1.5} /> Premium Atelier
          </p>
          <p className="font-serif text-5xl mb-2">$9.99<span className="text-xl text-ash">/mo</span></p>
          <p className="text-ash mb-10">Cancel anytime · billed monthly</p>
          <ul className="space-y-3 mb-10">
            {PREMIUM_FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-3 text-bone text-sm">
                <Check size={14} className="text-gold mt-1" strokeWidth={1.5} /> {f}
              </li>
            ))}
          </ul>
          <button
            onClick={subscribe}
            disabled={loading || user?.is_premium}
            className="crown-btn-primary w-full justify-center"
            data-testid="subscribe-btn"
          >
            {loading ? (
              <><Loader2 className="animate-spin" size={14} strokeWidth={1.5} /> Preparing checkout</>
            ) : user?.is_premium ? (
              <>You're Premium <Check size={14} strokeWidth={1.5} /></>
            ) : (
              <>Become a member <ArrowUpRight size={14} strokeWidth={1.5} /></>
            )}
          </button>
        </div>
      </div>

      <p className="font-overline text-ash text-center mt-12">Secure payments by Stripe · test mode</p>
    </main>
  );
}
